<template>
  <section class="overflow-hidden">
    <v-row v-if="error" no-gutters>
      <Error size="250" class="mt-5" :errorMsg="error"></Error>
    </v-row>
    <v-row v-if="success" no-gutters>
      <Success size="250" class="mt-5" :successMsg="success"></Success>
    </v-row>

    <v-row no-gutters justify="center">
      <v-col class="pa-5" cols="auto">
        <v-container id="container-login" max class="mx-auto">
          <v-row justify="center">
            <v-col cols="12" xs="12">
              <v-card class="mx-auto pa-3 card" width="80vw" max-width="90vw">
                <v-row no-gutters justify="center">
                  <h1 class="text-center">{{ $t("Contact.Title") }}</h1>
                </v-row>
                <v-row no-gutters justify="center">
                  <v-card-subtitle class="text-center">{{
                    $t("Contact.Subtitle")
                  }}</v-card-subtitle>
                </v-row>
                <v-divider class="mb-4 mt-8" />
                <ValidationObserver ref="observer">
                  <form id="formElem">
                    <TextInput
                      :label="labels.subject"
                      name="Subject"
                      rules="required|min:2|max:100"
                      :textValue.sync="formData.subject"
                      :success="subjectValid"
                    />
                    <TextArea
                      :label="labels.message"
                      name="Message"
                      rules="required|min:2|max:5000"
                      :textAreaValue.sync="formData.message"
                      :success="messageValid"
                    />
                    <EmailInput
                      :label="labels.email"
                      name="E-Mail"
                      :emailValue.sync="formData.email"
                    />
                    <v-divider class="mb-4 mt-2" />
                    <v-row justify="center">
                      <span>
                        <vue-recaptcha
                          sitekey="6Lf94MkZAAAAAA7PI7HlQUaxr0bBKOzG-UxtSyqU"
                          :loadRecaptchaScript="true"
                          @verify="onVerify"
                        ></vue-recaptcha>
                      </span>
                    </v-row>
                    <v-divider class="mb-4 mt-2" />
                    <v-row justify="center">
                      <SubmitButton
                        :click="submitForm"
                        :isDisabled="submitButtonIsDisabled"
                        :label="labels.submit"
                      />
                      <ClearButton :click="clearForm" :label="labels.clear" />
                    </v-row>
                  </form>
                </ValidationObserver>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-row>
      <Cards :cardsData="socialMedia" />
    </v-row>
  </section>
</template>

<script>
import { required, min, max, alpha } from "vee-validate/dist/rules";
import { ValidationObserver, extend, setInteractionMode } from "vee-validate";
import VueRecaptcha from "vue-recaptcha";
import SubmitButton from "./buttons/SubmitButton";
import ClearButton from "./buttons/ClearButton";
import EmailInput from "./inputs/EmailInput";
import TextArea from "./inputs/TextArea";
import TextInput from "./inputs/TextInput";
import axios from "axios";
import Success from "./shared/Success";
import Error from "./shared/Error";
import Cards from "./visualization/Cards";

setInteractionMode("eager");

extend("required", {
  ...required,
  message: "{_field_} can not be empty",
});

extend("alpha", {
  ...alpha,
  message: "{_field_} can only contain alphabetic characters",
});

extend("min", {
  ...min,
  message: "{_field_} must contain at least {length} characters",
});

extend("max", {
  ...max,
  message: "{_field_} may not be greater than {length} characters",
});

const initFromData = {
  subject: "",
  message: "",
  email: "",
  recaptchaValid: false,
};

export default {
  name: "SignUp",
  components: {
    ValidationObserver,
    EmailInput,
    SubmitButton,
    ClearButton,
    TextInput,
    TextArea,
    VueRecaptcha,
    Error,
    Success,
    Cards,
  },
  data() {
    return {
      name: "Contact",
      formData: Object.assign({}, initFromData),
      output: "",
      submitButtonDisabled: true,
    };
  },
  computed: {
    loading: {
      get() {
        return this.$store.getters["loading/loading"];
      },
      set(value) {
        this.$store.dispatch("loading/setLoading", value);
      },
    },
    error: {
      get() {
        return this.$store.getters["error/error"];
      },
      set(value) {
        this.$store.dispatch("error/setError", value);
      },
    },
    success: {
      get() {
        return this.$store.getters["success/success"];
      },
      set(value) {
        this.$store.dispatch("success/setSuccess", value);
      },
    },
    labels() {
      return {
        subject: this.$i18n.t("Form.Subject"),
        email: this.$i18n.t("Form.Email"),
        message: this.$i18n.t("Form.Message"),
        submit: this.$i18n.t("Form.Submit"),
        clear: this.$i18n.t("Form.Clear"),
      };
    },
    socialMedia() {
      return [
        {
          name: "Github",
          links: [
            {
              name: "@ Arkenfar",
              url: "https://github.com/arkenfar",
              icon: "mdi-github",
            },
          ],
        },
        {
          name: "Twitter",
          links: [
            {
              name: "@ Arkenfar",
              url: "https://twitter.com/arkenfar",
              icon: "mdi-twitter",
            },
          ],
        },
        {
          name: "Facebook",
          links: [
            {
              name: "@ Arkenfar",
              url: "https://www.facebook.com/arkenfar",
              icon: "mdi-facebook",
            },
          ],
        },
        {
          name: "Twitch",
          links: [
            {
              name: "@ Arkenfar",
              url: "https://www.twitch.tv/arkenfar",
              icon: "mdi-twitch",
            },
          ],
        },
        {
          name: "Medium",
          links: [
            {
              name: "@ Arkenfar",
              url: "https://medium.com/@arkenfar",
              icon: "mdi-alpha-m-box",
            },
          ],
        },
      ];
    },
    submitButtonIsDisabled: {
      get() {
        return this.submitButtonDisabled;
      },
      set(value) {
        this.submitButtonDisabled = value;
      },
    },
    subjectValid() {
      if (
        this.formData.subject.length >= 2 &&
        this.formData.subject.length <= 100
      ) {
        return true;
      }
      return false;
    },
    messageValid() {
      if (
        this.formData.message.length >= 2 &&
        this.formData.message.length <= 5000
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    async submitForm(e) {
      const isValid = await this.$refs.observer.validate();
      if (!isValid || !this.formData.recaptchaValid) {
        this.error =
          "Contact form does not meet requirements of the required fields.";

        return;
      }
      e.preventDefault();
      let currentObj = this;
      let { email, message, subject } = currentObj.formData;
      let formData = new FormData();
      formData.append("email", email);
      formData.append("message", message);
      formData.append("subject", subject);

      let apiUrl =
        "https://us-central1-alexanderfelipe-b9867.cloudfunctions.net/sendMail";
      let apiOptions = {
        method: "POST",
        url: apiUrl,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      };

      await axios(apiOptions)
        .then((response) => {
          currentObj.success = response.data.message;
          console.log(response);
          this.clearForm();
        })
        .catch((error) => {
          currentObj.error = error;
          console.log(error);
        });
    },
    clearForm() {
      this.formData = Object.assign({}, initFromData);
      this.formData.recaptchaValid = false;
      this.submitButtonIsDisabled = true;
      this.$refs.observer.reset();
    },
    onVerify(response) {
      if (response) {
        this.formData.recaptchaValid = true;
        this.submitButtonIsDisabled = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.card {
  border-radius: 25px;
  border: 2px solid var(--v-info-base);
}
</style>
